import HighchartsReact from 'highcharts-react-official';
import {
  SeriesAreaOptions,
  SeriesBarOptions,
  SeriesColumnOptions,
  SeriesHistogramOptions,
  SeriesLineOptions,
  SeriesOptionsType,
  SeriesPieOptions,
  SeriesWordcloudOptions,
} from 'highcharts/highcharts';
import isEqual from 'lodash.isequal';
import throttle from 'lodash.throttle';
import { CustomOnResize } from 'modules/Editor/components/BoundingBox/BoundingBox.types';
import { SeriesColorPickerProps } from 'modules/Editor/components/SideMenuSettings/ChartSettings/ChartSettings.types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Optional } from 'types/object.types';
import { parseStrictNumber } from 'utils/number';

import { validateSpreadsheetColumns, validateSpreadsheetRows } from './ChartWidget.helpers';
import { ChartDataSeries, ControlledHeaderType, UseHighchartsDataProps } from './ChartWidget.types';

const UPDATE_TIME = 100;

interface UseResponsiveHighchartsProps {
  chartRef: React.RefObject<HighchartsReact.RefObject> | null;
  heightPx: number;
  widthPx: number;
}

export const useResponsiveHighcharts = ({ chartRef, heightPx, widthPx }: UseResponsiveHighchartsProps) => {
  useEffect(() => {
    if (!chartRef?.current) return;

    chartRef.current.chart.setSize(widthPx, heightPx);
  }, [heightPx, widthPx, chartRef]);

  // keep the chart size in sync with widget size while resizing
  const customOnResize = useCallback(
    ({ onResize, event }: CustomOnResize) => {
      onResize(event);

      if (!chartRef?.current) return;
      const target = event.target as HTMLDivElement;
      chartRef.current.chart.setSize(target.clientWidth ?? event.width, target.clientHeight ?? event.height);
    },
    [chartRef],
  );

  return { customOnResize };
};

export type HighchartsSeriesData = Optional<SeriesOptionsType, 'type'> & {
  // This is partial type.
  // @see https://api.highcharts.com/highcharts/series
  data:
    | SeriesAreaOptions['data']
    | SeriesBarOptions['data']
    | SeriesColumnOptions['data']
    | SeriesLineOptions['data']
    | SeriesPieOptions['data']
    | SeriesWordcloudOptions['data']
    | SeriesHistogramOptions['data'];

  controlledHeader?: ControlledHeaderType;
};

interface UseHighchartsDataResult {
  categories: string[];
  series: HighchartsSeriesData[];
  colorPickerSeries: SeriesColorPickerProps['series'];

  /**
   * Custom key is used to remember the time of data changed.
   * This is required on highcharts wrapper to force an update on highcharts.
   *
   * This fix the bug that
   * when the data is changed from the number to NaN or empty string, the chart would not be updated
   */
  keyCustom: number;

  mostLengthyLabel: string;
}

/**
 * A hook to generate the 'series' requested by the Highcharts lib
 * from the 'seriesData' which is used by the handsontable
 * @param {object} props see UseHighchartsDataProps interface
 * @returns A 'series' object used by Highcharts.
 */
export const useHighchartsData = ({
  seriesData,
  seriesMeta,
  allowSingleSeries = false,
  forceSingleSeries,
  disableColumnHeader = false,
  disableRowHeader = false,
}: UseHighchartsDataProps): UseHighchartsDataResult => {
  // Get the row data with invalid rows removed.
  // Invalid rows are rows that are empty or have data that the highcharts cannot handle
  const { seriesData: validRows } = validateSpreadsheetRows(seriesData);

  // prev stores the previous return value of this hook
  const prev = useRef<UseHighchartsDataResult | null>(null);

  // Calculate the highcharts data from the seriesData
  const calculateHighchartsData: (data: ChartDataSeries) => UseHighchartsDataResult = useCallback(
    (validRows) => {
      const next = prev.current ?? {
        categories: [],
        series: [],
        colorPickerSeries: [],
        mostLengthyLabel: '',
        keyCustom: 0,
      };

      // Destruct and remove the first column, so we can still keep the type info
      const [rowHeaderColumn, ...validColumnData] = validateSpreadsheetColumns(validRows, {
        disableColumnHeader,
        disableRowHeader,
      });
      next['categories'] = rowHeaderColumn.data.map((value) => {
        // default value to empty string if column is null or undefined
        const dataValue = value ?? '';
        return dataValue.toString();
      });

      // No data, Generate empty highcharts data
      if (validRows.length === 0 || (!disableRowHeader && validColumnData.length === 0)) {
        next['series'] = [];
        next['mostLengthyLabel'] = '';
        return next;
      }

      let colorPickerSeries: UseHighchartsDataResult['colorPickerSeries'] = [];
      const colors = [...seriesMeta.colors];
      let mostLengthyLabel = '';

      if (disableRowHeader) {
        // Generate single series highcharts data
        const data = rowHeaderColumn.data.reduce((data, currentValue) => {
          try {
            const validatedNumber = parseStrictNumber(currentValue);
            data.push(validatedNumber);

            if (validatedNumber.toString().length > mostLengthyLabel.length) {
              mostLengthyLabel = validatedNumber.toString();
            }
          } catch (error) {
            // The parseStrictNumber would throw an error when a string cannot be converted to a finite number
            // However, It is a valid case in this function, and we just need to ignore those numbers.
          }

          return data;
        }, [] as number[]);

        // Note: Currently only the Histogram can ignore the row header, so only the Histogram chart would be here
        // On Histogram chart, we need only one color for all columns
        colorPickerSeries.push({
          name: 'Data color',
          color: colors[0],
        });

        next['series'] = [{ data }];
      } else if (forceSingleSeries || (allowSingleSeries && validColumnData.length === 1)) {
        // Generate single series highcharts data using the second column as the data
        const { data: numData } = validColumnData[0];
        const data = numData.map((y, index) => {
          const colorPickerToken = {
            name: (rowHeaderColumn.data[index] ?? '').toString(),
            color: colors[index % colors.length],
          };
          colorPickerSeries.push(colorPickerToken);

          if (y.toString().length > mostLengthyLabel.length) {
            mostLengthyLabel = y.toString();
          }

          return {
            ...colorPickerToken,
            y: Number.isNaN(y) ? null : y,
          };
        });

        next['series'] = [{ data }];
      } else {
        // Generate multiple series highcharts data
        next['series'] = validColumnData.map(({ controlledHeader, name, data }, index) => {
          const colorPickerToken = {
            name: name ?? '',
            color: colors[index % colors.length],
          };
          colorPickerSeries.push(colorPickerToken);

          return {
            ...colorPickerToken,
            controlledHeader,
            data: data.map((value) => {
              if (value.toString().length > mostLengthyLabel.length) {
                mostLengthyLabel = value.toString();
              }

              return Number.isNaN(value) ? null : value;
            }),
          };
        });
      }

      next['colorPickerSeries'] = colorPickerSeries;
      next['mostLengthyLabel'] = mostLengthyLabel;
      return next;
    },
    [allowSingleSeries, disableColumnHeader, disableRowHeader, forceSingleSeries, seriesMeta.colors],
  );

  // Use throttle and useState to avoid too many re-renders when the user is pasting,
  // or when a re-render is triggered by other reasons
  const [returnVal, setReturnVal] = useState(prev.current ?? calculateHighchartsData(validRows));
  if (!prev.current) prev.current = returnVal; // Initialize prev.current on the first render

  // The throttledUpdate is used to update the returnVal state, which avoids render error: too many re-renders
  const throttledUpdate = useRef<((caller: typeof calculateHighchartsData, data: ChartDataSeries) => void) | null>();
  if (!throttledUpdate.current) {
    throttledUpdate.current = throttle((calculateFn: typeof calculateHighchartsData, data: ChartDataSeries) => {
      const { categories, colorPickerSeries, series, mostLengthyLabel } = calculateFn(data);

      setReturnVal((prevReturnVal) => {
        let current = prevReturnVal;

        if (!isEqual(current.categories, categories)) {
          current = { ...current, categories };
        }

        if (!isEqual(current.series, series)) {
          current = { ...current, series };
        }

        if (!isEqual(current.colorPickerSeries, colorPickerSeries)) {
          current = { ...current, colorPickerSeries };
        }

        if (current.mostLengthyLabel !== mostLengthyLabel) {
          current = { ...current, mostLengthyLabel: mostLengthyLabel };
        }

        prev.current = current;
        return current;
      });
    }, UPDATE_TIME);
  }

  // Update the returnVal state on every render
  throttledUpdate.current(calculateHighchartsData, validRows);

  return returnVal;
};
